import type { ReactNode } from 'react';
import { Box, Button, Stack, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Error as ErrorIcon,
  Description as ReportIcon,
  PlayArrow as RunIcon,
  FolderOff as NoDocumentIcon,
} from '@mui/icons-material';
import { borderRadius, getModernButton, layoutGap } from '../../../theme/theme';
import {
  BodyText,
  CaptionText,
  CompactPageTitle,
  OverlineText,
} from '../../../shared/ui/typography';
import { OperationProgressPlaceholder } from '../../../shared/ui/progress';
import { validationCopy } from '../copy/validationCopy';
import ValidationReadyEmptyState from './ValidationReadyEmptyState';

interface ValidationEmptyStateProps {
  variant: 'not-connected' | 'connecting' | 'empty' | 'stale' | 'loading' | 'no-document';
  onRun?: () => void;
  disableRun?: boolean;
}

interface EmptyStateHeroProps {
  icon: ReactNode;
  tone: 'error' | 'info';
  title: string;
  description: string;
  caption?: string;
  stepsTitle?: string;
  steps?: string[];
}

function EmptyStateHero({ icon, tone, title, description, caption, stepsTitle, steps }: EmptyStateHeroProps) {
  const theme = useTheme();
  const toneColor = tone === 'error' ? theme.palette.error.main : theme.palette.primary.main;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        textAlign: 'center',
        py: 6,
        px: 3,
      }}
    >
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: '50%',
          backgroundColor: alpha(toneColor, 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', color: toneColor, opacity: 0.8, '& svg': { fontSize: 48 } }}>
          {icon}
        </Box>
      </Box>

      <CompactPageTitle sx={{ color: theme.palette.text.primary, mb: 1 }}>
        {title}
      </CompactPageTitle>

      <BodyText color="text.secondary" sx={{ maxWidth: 420 }}>
        {description}
      </BodyText>

      {steps && steps.length > 0 ? (
        <Box
          sx={{
            mt: 3,
            maxWidth: 420,
            width: '100%',
            textAlign: 'left',
            px: 2,
            py: 1.5,
            borderRadius: `${borderRadius.md}px`,
            border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.32 : 0.2)}`,
            backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.86 : 0.32),
          }}
        >
          {stepsTitle ? (
            <OverlineText sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
              {stepsTitle}
            </OverlineText>
          ) : null}
          <Stack component="ol" spacing={layoutGap.comfort} sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {steps.map((step, index) => (
              <Stack
                component="li"
                key={step}
                direction="row"
                spacing={layoutGap.comfort}
                alignItems="flex-start"
              >
                <CaptionText
                  component="span"
                  sx={{
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    mt: '1px',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: alpha(toneColor, 0.1),
                    color: toneColor,
                  }}
                >
                  {index + 1}
                </CaptionText>
                <BodyText secondary sx={{ minWidth: 0 }}>
                  {step}
                </BodyText>
              </Stack>
            ))}
          </Stack>
        </Box>
      ) : null}

      {caption ? (
        <CaptionText color="text.secondary" sx={{ mt: 2.5 }}>
          {caption}
        </CaptionText>
      ) : null}
    </Box>
  );
}

export default function ValidationEmptyState({ variant, onRun, disableRun }: ValidationEmptyStateProps) {
  const theme = useTheme();
  const buttonStyles = getModernButton(theme);
  const copy = validationCopy.emptyState;

  if (variant === 'not-connected') {
    return (
      <EmptyStateHero
        icon={<ErrorIcon />}
        tone="error"
        title={copy.notConnected.title}
        description={copy.notConnected.description}
        caption={copy.notConnected.caption}
        stepsTitle={copy.notConnected.stepsTitle}
        steps={copy.notConnected.steps}
      />
    );
  }

  if (variant === 'no-document') {
    return (
      <EmptyStateHero
        icon={<NoDocumentIcon />}
        tone="info"
        title={copy.noDocument.title}
        description={copy.noDocument.description}
        stepsTitle={copy.noDocument.stepsTitle}
        steps={copy.noDocument.steps}
      />
    );
  }

  if (variant === 'connecting') {
    return (
      <Box sx={{ minHeight: 400, display: 'flex' }}>
        <OperationProgressPlaceholder
          model={{
            title: copy.connecting.title,
            message: copy.connecting.message,
            detail: copy.connecting.detail,
            status: 'pending',
            percent: null,
            phase: copy.connecting.phase,
            locked: true,
            steps: [
              { id: 'connect', label: copy.connecting.steps[0], status: 'current' },
              { id: 'fetch', label: copy.connecting.steps[1], status: 'pending' },
              { id: 'prepare', label: copy.connecting.steps[2], status: 'pending' },
            ],
          }}
        />
      </Box>
    );
  }

  if (variant === 'loading') {
    return (
      <Box sx={{ minHeight: 400, display: 'flex' }}>
        <OperationProgressPlaceholder
          model={{
            title: copy.loading.title,
            message: copy.loading.message,
            detail: copy.loading.detail,
            status: 'running',
            percent: null,
            phase: copy.loading.phase,
            locked: true,
            steps: [
              { id: 'connect', label: copy.loading.steps[0], status: 'complete' },
              { id: 'fetch', label: copy.loading.steps[1], status: 'current' },
              { id: 'prepare', label: copy.loading.steps[2], status: 'pending' },
            ],
          }}
        />
      </Box>
    );
  }

  if (variant === 'stale') {
    return (
      <Box
        data-testid="validation-report-stale"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          px: 3,
          textAlign: 'center',
          minHeight: '400px',
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            backgroundColor: alpha(theme.palette.warning.main, 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2.5,
          }}
        >
          <ReportIcon
            sx={{ fontSize: 48, color: theme.palette.warning.main, opacity: 0.8 }}
          />
        </Box>

        <CompactPageTitle sx={{ color: theme.palette.text.primary, mb: 1 }}>
          {copy.stale.title}
        </CompactPageTitle>

        <BodyText color="text.secondary" sx={{ mb: 3, maxWidth: 420 }}>
          {copy.stale.description}
        </BodyText>

        <Stack direction="row" spacing={layoutGap.row} useFlexGap flexWrap="wrap" justifyContent="center">
          <Button
            variant="contained"
            size="large"
            startIcon={<RunIcon />}
            onClick={onRun}
            disabled={disableRun}
            sx={{ ...buttonStyles.contained, px: 4, py: 1 }}
          >
            {copy.stale.run}
          </Button>
        </Stack>
      </Box>
    );
  }

  return <ValidationReadyEmptyState onRun={onRun} disableRun={disableRun} />;
}
